import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import BhCardEffectOverlay, { playBhCardSound } from './BhCardEffectOverlay';
import { useScreenShake, SCENE } from '../../lib/battle3d';
import RoseGarden from '../arena/RoseGarden';
import BhTable from '../arena/BhTable';
import Card3D from '../arena/Card3D';
import GameIntro from '../arena/GameIntro';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bh`;

// ─── Card definitions ─────────────────────────────────────────────────────────
const BH_CARDS = {
  1:  { name:'NO',                icon:'💔', sub:'Perdi 1 cuore',                bg:'linear-gradient(145deg,#ff6b9d,#c2185b)', border:'#ff4081', glow:'rgba(255,64,129,0.7)' },
  2:  { name:'DUE DI PICCHE',     icon:'♠️', sub:'Perdi 2 cuori',                bg:'linear-gradient(145deg,#8b5cf6,#5b21b6)', border:'#a855f7', glow:'rgba(168,85,247,0.7)' },
  3:  { name:'CORNA',             icon:'🤘', sub:'Perdi 3 cuori',                bg:'linear-gradient(145deg,#ef4444,#b91c1c)', border:'#f87171', glow:'rgba(239,68,68,0.7)'  },
  4:  { name:'HOT',               icon:'🔥', sub:'Nessun effetto',               bg:'linear-gradient(145deg,#f97316,#c2410c)', border:'#fb923c', glow:'rgba(249,115,22,0.6)' },
  5:  { name:'LIKE',              icon:'👍', sub:'Nessun effetto',               bg:'linear-gradient(145deg,#22c55e,#15803d)', border:'#4ade80', glow:'rgba(34,197,94,0.6)'  },
  6:  { name:'SMILE',             icon:'😊', sub:'Nessun effetto',               bg:'linear-gradient(145deg,#eab308,#a16207)', border:'#fde047', glow:'rgba(234,179,8,0.6)'  },
  7:  { name:'CUORE DI GHIACCIO', icon:'🧊', sub:'Protegge dai malus',           bg:'linear-gradient(145deg,#38bdf8,#0369a1)', border:'#7dd3fc', glow:'rgba(56,189,248,0.7)' },
  8:  { name:'DONNA KISS',        icon:'💋', sub:'Dai 1 cuore al precedente',    bg:'linear-gradient(145deg,#f472b6,#be185d)', border:'#f9a8d4', glow:'rgba(244,114,182,0.7)'},
  9:  { name:'UOMO KISS',         icon:'💋', sub:'Dai 1 cuore al successivo',    bg:'linear-gradient(145deg,#fb7185,#e11d48)', border:'#fda4af', glow:'rgba(251,113,133,0.7)'},
  10: { name:'SEX',               icon:'❤️‍🔥', sub:'Guadagni 1 cuore',          bg:'linear-gradient(145deg,#ec4899,#9d174d)', border:'#f9a8d4', glow:'rgba(236,72,153,0.8)' },
};


const BH_ICONS = {
  1:'💔', 2:'♠️', 3:'🤘', 4:'🔥', 5:'👍',
  6:'😊', 7:'🧊', 8:'💋', 9:'💋', 10:'❤️‍🔥'
};

const BH_BACK = {
  deep: '#fbcfe8', mid: '#fff1f7', rim: '#ec4899', gold: '#be185d',
  glow: 'rgba(236,72,153,0.45)', emblem: '❤',
  shade: 'rgba(190,24,93,0.20)', sheen: 'rgba(255,255,255,0.55)',
};
const BH_EDGE = ['#fff1f6', '#f0a8c6', '#c76b90'];

// Quanto trema la scena per ogni carta
const BH_SHAKE = { 1: 0.8, 2: 1.1, 3: 1.5, 4: 0, 5: 0, 6: 0, 7: 0.5, 8: 0.6, 9: 0.6, 10: 0.7 };

// ─── BhCard: il corpo 3D condiviso con l'illustrazione sul fronte ────────────
const BhCard = ({ card, index, onPick, canPick, mobile, isRevealing }) => {
  const revealed = card.revealed;
  // Tiene l'ultimo valore valido: dopo la giocata il valore torna null
  const [lastValue, setLastValue] = useState(card.value);
  useEffect(() => { if (card.value !== null) setLastValue(card.value); }, [card.value]);
  const displayValue = card.value ?? lastValue;
  const meta = BH_CARDS[displayValue];

  const front = (
    <div style={{ position: 'absolute', inset: 0, background: '#1a0410' }}>
      {displayValue && (
        <img
          src={`/cards/bh/card_${displayValue}.png`}
          alt={meta?.name || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          draggable={false}
        />
      )}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -70, rotateZ: index % 2 ? 9 : -9 }}
      animate={{ opacity: 1, y: 0, rotateZ: 0 }}
      transition={{ delay: 0.25 + index * 0.11, type: 'spring', stiffness: 150, damping: 15 }}
      style={{ transformStyle: 'preserve-3d' }}
    >
      <Card3D
        revealed={revealed}
        index={index}
        onPick={onPick}
        canPick={canPick}
        isRevealing={isRevealing}
        mobile={mobile}
        back={BH_BACK}
        edge={BH_EDGE}
        frontBase="#1a0410"
        tone={meta?.border || '#ec4899'}
        front={front}
        prompt="SCEGLI"
      />
    </motion.div>
  );
};

// ─── PlayerBox — bright, readable ────────────────────────────────────────────
const BhPlayerBox = ({ player, isActive, variant = 'full' }) => {
  // variant: 'full' = desktop all players same size, 'compact' = mobile opponents
  const isMobile = variant === 'compact';
  const hearts = Array.from({ length:5 }, (_,i) => i < player.crowns);
  const shields = Array.from({ length:2 }, (_,i) => i < player.shields);

  if (isMobile) {
    // Compact mobile — TLK style: icon on top + number box below
    const BhStatCol = ({ icon, isImg, value }) => (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
        {isImg
          ? <img src={icon} alt="" style={{ width:20, height:20, objectFit:'cover', borderRadius:3,
              filter:'drop-shadow(0 0 4px rgba(56,189,248,0.6))' }} draggable={false}/>
          : <span style={{ fontSize:20, lineHeight:1,
              filter:'drop-shadow(0 1px 3px rgba(0,0,0,0.2))' }}>{icon}</span>
        }
        <div style={{ minWidth:24, height:18, background:'linear-gradient(180deg,#fff0f6,#fce7f3)',
          border:'1px solid rgba(236,72,153,0.4)', borderRadius:4,
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'inset 0 1px 3px rgba(0,0,0,0.1)' }}>
          <span style={{ fontSize:11, fontWeight:900, color:'#be185d',
            fontFamily:"'Cinzel',serif", lineHeight:1 }}>{value}</span>
        </div>
      </div>
    );

    return (
      <div style={{
        position:'relative', borderRadius:12, overflow:'hidden',
        background: isActive ? 'linear-gradient(160deg,#fff0f6,#fce7f3)' : 'linear-gradient(160deg,#fff8fa,#fdf4f6)',
        border:`1.5px solid ${isActive ? '#ec4899' : 'rgba(236,72,153,0.25)'}`,
        boxShadow: isActive
          ? '0 0 0 2px rgba(236,72,153,0.3),0 0 20px rgba(236,72,153,0.2),0 4px 16px rgba(0,0,0,0.08)'
          : '0 2px 10px rgba(236,72,153,0.08)',
        padding:'6px 8px', minWidth:82,
      }}>
        {/* Top gloss */}
        <div style={{ position:'absolute', top:0, left:0, right:0, height:'42%',
          background:'linear-gradient(to bottom,rgba(255,255,255,0.5),transparent)',
          borderRadius:'12px 12px 0 0', pointerEvents:'none' }} />

        {/* Name */}
        <div style={{ fontSize:7, fontWeight:700, color:player.is_frozen?'#9ca3af':'#831843',
          fontFamily:"'Cinzel',serif", textAlign:'center', overflow:'hidden',
          textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:5 }}>
          {player.username}
        </div>

        {/* Divider */}
        <div style={{ height:1, background:'linear-gradient(to right,transparent,rgba(236,72,153,0.3),transparent)', marginBottom:5 }} />

        {/* Stats row */}
        <div style={{ display:'flex', gap:4, alignItems:'flex-end', justifyContent:'center' }}>
          <BhStatCol icon="❤️" value={player.crowns} />
          <BhStatCol icon="/cards/bh/card_7.png" isImg value={player.shields} />
          <BhStatCol icon="🃏" value={player.jolly} />
        </div>

        {/* TURN dot */}
        {isActive && !player.is_frozen && (
          <motion.div animate={{ opacity:[0.4,1,0.4], scale:[0.8,1.3,0.8] }} transition={{ duration:0.85, repeat:Infinity }}
            style={{ position:'absolute', top:5, right:5, width:6, height:6, borderRadius:'50%',
              background:'#ec4899', boxShadow:'0 0 8px #ec4899' }} />
        )}
        {/* Active pulse */}
        {isActive && !player.is_frozen && (
          <motion.div animate={{ opacity:[0.15,0.6,0.15] }} transition={{ duration:0.85, repeat:Infinity }}
            style={{ position:'absolute', inset:0, borderRadius:12, border:'2px solid #ec4899', pointerEvents:'none' }} />
        )}
        {/* Eliminated */}
        {player.is_frozen && (
          <div style={{ position:'absolute', inset:0, background:'rgba(255,255,255,0.88)', borderRadius:12,
            display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:2 }}>
            <div style={{ position:'absolute', inset:0, display:'flex', justifyContent:'space-evenly', padding:'0 6px' }}>
              {[...Array(5)].map((_,i)=><div key={i} style={{ width:1.5, background:'rgba(190,24,93,0.2)', borderRadius:1 }}/>)}
            </div>
            <span style={{ fontSize:14, position:'relative', zIndex:1 }}>💔</span>
            <span style={{ fontSize:7, fontWeight:900, color:'#9ca3af', position:'relative', zIndex:1 }}>OUT</span>
          </div>
        )}
      </div>
    );
  }

  // Full size — same for all players desktop
  return (
    <div style={{
      position:'relative', borderRadius:14, overflow:'hidden',
      background: isActive ? 'linear-gradient(160deg,#fff0f6,#fce7f3)' : 'linear-gradient(160deg,#fff5f8,#fdf2f8)',
      border:`2px solid ${isActive ? '#ec4899' : 'rgba(236,72,153,0.3)'}`,
      boxShadow: isActive
        ? '0 0 0 3px rgba(236,72,153,0.25),0 8px 28px rgba(236,72,153,0.2),0 2px 8px rgba(0,0,0,0.08)'
        : '0 2px 12px rgba(236,72,153,0.08)',
      padding:'12px 16px', minWidth:220,
    }}>
      <div style={{ height:3, background: isActive
        ? 'linear-gradient(to right,#f472b6,#ec4899,#be185d)'
        : 'linear-gradient(to right,#fda4af,#f9a8d4)',
        marginBottom:10, borderRadius:2 }} />

      {/* Name */}
      <div style={{ fontSize:12, fontWeight:800,
        color: player.is_frozen ? '#9ca3af' : '#831843',
        fontFamily:"'Cinzel',serif", textAlign:'center',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:8 }}>
        {player.username}
      </div>

      {/* Hearts — max 5 */}
      <div style={{ display:'flex', justifyContent:'center', gap:4, marginBottom:6 }}>
        {hearts.map((full,i) => (
          <span key={i} style={{ fontSize:18, opacity:full?1:0.12,
            filter:full?'drop-shadow(0 1px 3px rgba(239,68,68,0.5))':'none',
            transition:'all 0.3s' }}>❤️</span>
        ))}
      </div>

      {/* Ice hearts — max 2 using card_7.png icon */}
      <div style={{ display:'flex', justifyContent:'center', gap:6, marginBottom:6 }}>
        {shields.map((full,i) => (
          <div key={i} style={{ width:22, height:22, borderRadius:4, overflow:'hidden',
            opacity:full?1:0.12,
            filter:full?'drop-shadow(0 1px 4px rgba(56,189,248,0.6))':'grayscale(1)',
            transition:'all 0.3s' }}>
            <img src="/cards/bh/card_7.png" alt="ghiaccio"
              style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          </div>
        ))}
      </div>

      {/* Jolly */}
      <div style={{ display:'flex', justifyContent:'center', gap:4 }}>
        {Array.from({length:2},(_,i) => (
          <span key={i} style={{ fontSize:15, opacity:i<player.jolly?1:0.12,
            filter:i<player.jolly?'drop-shadow(0 1px 3px rgba(139,92,246,0.5))':'none' }}>🃏</span>
        ))}
      </div>

      {/* TURN badge */}
      {isActive && !player.is_frozen && (
        <motion.div animate={{ opacity:[0.7,1,0.7] }} transition={{ duration:0.9, repeat:Infinity }}
          style={{ position:'absolute', top:8, right:8,
            background:'#ec4899', borderRadius:6, padding:'2px 8px',
            fontSize:8, fontWeight:900, color:'#fff', letterSpacing:'0.1em' }}>
          TURN
        </motion.div>
      )}

      {isActive && !player.is_frozen && (
        <motion.div animate={{ opacity:[0.2,0.6,0.2] }} transition={{ duration:0.9, repeat:Infinity }}
          style={{ position:'absolute', inset:0, borderRadius:14, border:'3px solid #ec4899', pointerEvents:'none' }} />
      )}

      {player.is_frozen && (
        <div style={{ position:'absolute', inset:0, background:'rgba(255,255,255,0.88)', borderRadius:14,
          display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:4 }}>
          <span style={{ fontSize:28 }}>💔</span>
          <span style={{ fontSize:10, fontWeight:900, color:'#9ca3af', letterSpacing:'0.1em' }}>ELIMINATO</span>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const BhGame = ({ user }) => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [revealingCard, setRevealingCard] = useState(null);
  const [cardEffect, setCardEffect] = useState(null);
  const [mobile, setMobile] = useState(window.innerWidth < 640);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem(`bh_intro_${gameId}`) !== 'done');
  const { shakeControls, shake } = useScreenShake();
  const pollingRef = useRef(null);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const loadGame = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/game/${gameId}`);
      setGameState(res.data); setLoading(false);
    } catch { toast.error('Errore caricamento partita'); }
  }, [gameId]);

  useEffect(() => {
    loadGame();
    pollingRef.current = setInterval(loadGame, 2500);
    const ch = supabase.channel(`bh_${gameId}`)
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'bh_game_sessions', filter:`id=eq.${gameId}` },
        (p) => setGameState(p.new)).subscribe();
    return () => { clearInterval(pollingRef.current); ch.unsubscribe(); supabase.removeChannel(ch); };
  }, [loadGame, gameId]);

  const handlePickCard = async (cardIndex) => {
    if (actionInProgress) return;
    setActionInProgress(true);
    setRevealingCard(cardIndex);
    try {
      const res = await axios.post(`${API}/game/pick-card`, { game_id:gameId, user_id:user.user_id, card_index:cardIndex });
      setFeedback(res.data.message);
      // Play sound and show effect
      const cv = res.data.card_value;
      if (cv) {
        playBhCardSound(cv);
        setCardEffect(cv);
        setTimeout(() => setCardEffect(null), 1800);
        const power = BH_SHAKE[cv] ?? 0;
        if (power) setTimeout(() => shake(power), 700);
      }
      setTimeout(() => { setFeedback(null); setRevealingCard(null); }, 2500);
      await loadGame();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
      setRevealingCard(null);
    } finally { setActionInProgress(false); }
  };

  const handleUseJolly = async () => {
    if (actionInProgress) return;
    setActionInProgress(true);
    try {
      await axios.post(`${API}/game/use-jolly`, { game_id:gameId, user_id:user.user_id });
      setFeedback('Turno saltato con il Jolly!');
      setTimeout(() => setFeedback(null), 2500);
      await loadGame();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setActionInProgress(false); }
  };

  // Shuffle display values once per round (stable, no flickering)
  // Must be before early return to respect rules of hooks
  const tableDisplayValues = useMemo(() => {
    const onTable = gameState?.cards_on_table || [];
    const vals = onTable.filter(v => v !== null);
    for (let i = vals.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [vals[i], vals[j]] = [vals[j], vals[i]];
    }
    let vi = 0;
    return onTable.map(v => v === null ? null : vals[vi++]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(gameState?.cards_on_table || []).join(',')]);

  if (loading || !gameState) return (
    <div style={{ width:'100dvw', height:'100dvh', position:'relative', overflow:'hidden', background:'#fff0f6' }}>
      <RoseGarden mobile={mobile} dimmed />
      <div style={{ position:'relative', zIndex:2, height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:14 }}>
        <motion.div
          animate={{ scale:[1,1.18,1] }} transition={{ duration:1.2, repeat:Infinity, ease:'easeInOut' }}
          style={{ fontSize:46, filter:'drop-shadow(0 6px 16px rgba(236,72,153,0.5))' }}>
          ❤️
        </motion.div>
        <div style={{ fontFamily:"'Cinzel',serif", fontSize:16, fontWeight:800, letterSpacing:'0.16em', color:'#be185d' }}>
          Il giardino si apre…
        </div>
      </div>
    </div>
  );

  const players = gameState.players || [];
  const currentPlayer = players[gameState.current_turn];
  const myPlayer = players.find(p => p.user_id === user.user_id);
  const isMyTurn = currentPlayer?.user_id === user.user_id;
  const canAct = isMyTurn && !myPlayer?.is_frozen && !actionInProgress;
  const cards = (gameState.cards_on_table || []).map((v,i) => ({
    value: v, revealed: v === null || revealingCard === i
  }));



  const myIndex = players.findIndex(p => p.user_id === user.user_id);
  const pos = {};
  players.forEach((p,i) => {
    const rel = (i - myIndex + players.length) % players.length;
    pos[['bottom','right','top','left'][rel]] = p;
  });

  // Winner screen
  if (gameState.status === 'finished') {
    const winner = players.find(p => p.user_id === gameState.winner);
    const isMe = winner?.user_id === user.user_id;
    return (
      <div style={{ minHeight:'100dvh', background:'linear-gradient(135deg,#fce7f3,#fff0f6)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <motion.div initial={{ scale:0.2, opacity:0, y:60 }} animate={{ scale:1, opacity:1, y:0 }}
          transition={{ type:'spring', stiffness:180, damping:18 }}
          style={{ background:'#fff', borderRadius:28, padding:'48px clamp(24px,6vw,56px)',
            border:`3px solid ${isMe?'#ec4899':'#fda4af'}`,
            boxShadow:isMe?'0 0 60px rgba(236,72,153,0.3),0 20px 60px rgba(0,0,0,0.1)':'0 20px 60px rgba(0,0,0,0.1)',
            textAlign:'center', maxWidth:380, width:'100%' }}>
          <motion.div animate={{ scale:isMe?[1,1.2,1]:1 }} transition={{ duration:1, repeat:Infinity }}
            style={{ fontSize:72, marginBottom:16 }}>{isMe?'💕':'💔'}</motion.div>
          <h1 style={{ fontFamily:"'Cinzel',serif", fontSize:'clamp(22px,5vw,36px)', fontWeight:900,
            margin:'0 0 10px', color: isMe?'#be185d':'#9ca3af' }}>
            {isMe?'Hai Vinto!':(winner?.username||'Fine')}
          </h1>
          <p style={{ color:'#9ca3af', fontSize:14, margin:'0 0 32px' }}>
            {isMe?"Il tuo cuore ha resistito.":`${winner?.username} ha vinto la partita.`}
          </p>
          <motion.button onClick={() => navigate('/games')} whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}
            style={{ background:'linear-gradient(135deg,#ec4899,#be185d)', border:'none', borderRadius:14,
              padding:'14px 36px', fontSize:14, fontWeight:900, color:'#fff', cursor:'pointer',
              fontFamily:"'Cinzel',serif", letterSpacing:'0.08em', textTransform:'uppercase',
              boxShadow:'0 4px 20px rgba(236,72,153,0.4)' }}>
            Torna ai Giochi
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const isMobileView = mobile;

  const BG = <RoseGarden mobile={mobile} />;

  // Dove siede ogni giocatore attorno al tavolo, nello spazio 3D
  const seat = (p) => (mobile
    ? { top:'translateZ(-70px)', left:'rotateY(14deg)', right:'rotateY(-14deg)', bottom:'translateZ(16px)' }
    : {
        top:'translateZ(-230px) rotateX(8deg)',
        left:'rotateY(21deg) translateZ(-30px)',
        right:'rotateY(-21deg) translateZ(-30px)',
        bottom:'translateZ(45px) rotateX(-5deg)',
      })[p];

  return (
    <div style={{ width:'100dvw', height:'100dvh', position:'relative', overflow:'hidden', background:'#fff0f6' }}>
      {BG}

      <motion.div animate={shakeControls} style={{
        position:'relative', zIndex:10, height:'100%',
        perspective: mobile ? SCENE.perspectiveMobile : SCENE.perspective,
        perspectiveOrigin:'50% 38%',
      }}>
        <div style={{ height:'100%', display:'flex', flexDirection:'column', padding: mobile?'8px 8px':'44px 30px 20px', gap:6, transformStyle:'preserve-3d' }}>

          {/* TOP */}
          <div style={{ display:'flex', justifyContent:'center', flexShrink:0, transformStyle:'preserve-3d' }}>
            {pos.top && (
              <div style={{ transform:seat('top'), transformStyle:'preserve-3d' }}>
                <BhPlayerBox player={pos.top} isActive={currentPlayer?.user_id===pos.top.user_id} variant={isMobileView?'compact':'full'}/>
              </div>
            )}
          </div>

          {/* MIDDLE */}
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, minHeight:0, transformStyle:'preserve-3d' }}>
            <div style={{ flexShrink:0, transform:seat('left'), transformStyle:'preserve-3d' }}>
              {pos.left && <BhPlayerBox player={pos.left} isActive={currentPlayer?.user_id===pos.left.user_id} variant={isMobileView?'compact':'full'}/>}
            </div>

            <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, minWidth:0 }}>

              {/* Targa: cosa resta sul tavolo */}
              <div style={{
                display:'inline-block',
                borderRadius: mobile?10:14, padding: mobile?'6px 16px':'9px 26px',
                background:'rgba(255,255,255,0.9)', backdropFilter:'blur(8px)',
                border:'2px solid rgba(236,72,153,0.35)',
                boxShadow:'0 6px 22px rgba(190,24,93,0.18), inset 0 1px 0 rgba(255,255,255,0.9)',
              }}>
                <div style={{ fontSize: mobile?7:9, fontWeight:800, letterSpacing:'0.22em',
                  color:'rgba(190,24,93,0.6)', textTransform:'uppercase', textAlign:'center', marginBottom: mobile?2:4 }}>
                  ❥ Sul Tavolo ❥
                </div>
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap: mobile?10:20 }}>
                  {tableDisplayValues.map((v,i) => (
                    <motion.span key={i}
                      animate={cards[i]?.revealed ? { opacity:0.3, scale:0.85 } : { opacity:1, scale:1 }}
                      transition={{ duration:0.4 }}
                      style={{
                        fontSize: mobile?20:30,
                        filter: cards[i]?.revealed ? 'grayscale(1)' : 'drop-shadow(0 2px 6px rgba(236,72,153,0.5))',
                      }}>
                      {v ? (BH_ICONS[v] || v) : '·'}
                    </motion.span>
                  ))}
                </div>
              </div>

              {/* Il tavolo di velluto */}
              <BhTable mobile={mobile} active={canAct}>
                <div style={{
                  display:'grid',
                  gridTemplateColumns: mobile?'repeat(2,auto)':'repeat(4,auto)',
                  columnGap: mobile?12:22, rowGap: mobile?16:26,
                  justifyContent:'center', justifyItems:'center', transformStyle:'preserve-3d',
                }}>
                  {cards.map((card,i) => (
                    <BhCard key={i} card={card} index={i} onPick={handlePickCard}
                      canPick={canAct && !card.revealed} mobile={mobile} isRevealing={revealingCard===i}/>
                  ))}
                </div>
              </BhTable>

              {/* Feedback */}
              <div style={{ minHeight:32, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <AnimatePresence mode="wait">
                  {feedback && (
                    <motion.div key="fb"
                      initial={{ opacity:0, y:8, scale:0.9 }} animate={{ opacity:1, y:0, scale:1 }} exit={{ opacity:0 }}
                      style={{ background:'white', border:'2px solid #f9a8d4',
                        borderRadius:20, padding:'7px 22px',
                        color:'#be185d', fontSize:mobile?12:14, fontWeight:700,
                        boxShadow:'0 6px 22px rgba(236,72,153,0.25)', textAlign:'center' }}>
                      {feedback}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {!isMyTurn && !myPlayer?.is_frozen && (
                <div style={{ background:'rgba(255,255,255,0.85)', backdropFilter:'blur(8px)',
                  border:'1px solid rgba(236,72,153,0.25)', borderRadius:9999,
                  padding:'7px 22px', color:'#9d174d', fontSize:mobile?12:14, fontWeight:600 }}>
                  Turno di <span style={{ color:'#ec4899', fontWeight:800 }}>{currentPlayer?.username}</span>...
                </div>
              )}
            </div>

            <div style={{ flexShrink:0, transform:seat('right'), transformStyle:'preserve-3d' }}>
              {pos.right && <BhPlayerBox player={pos.right} isActive={currentPlayer?.user_id===pos.right.user_id} variant={isMobileView?'compact':'full'}/>}
            </div>
          </div>

          {/* BOTTOM */}
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8, flexShrink:0, transformStyle:'preserve-3d' }}>
            {myPlayer && myPlayer.jolly > 0 && canAct && (
              <motion.button onClick={handleUseJolly} disabled={actionInProgress}
                whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
                animate={{ boxShadow:['0 0 8px rgba(236,72,153,0.3)','0 0 24px rgba(236,72,153,0.7)','0 0 8px rgba(236,72,153,0.3)'] }}
                transition={{ duration:1.6, repeat:Infinity }}
                style={{ display:'flex', alignItems:'center', gap:8, padding:mobile?'7px 16px':'9px 22px',
                  background:'linear-gradient(135deg,#ec4899,#be185d)', border:'none', borderRadius:20,
                  cursor:'pointer', boxShadow:'0 4px 16px rgba(236,72,153,0.4)' }}>
                <span style={{ fontSize:mobile?14:18 }}>🃏</span>
                <span style={{ fontSize:mobile?11:13, fontWeight:800, color:'#fff', letterSpacing:'0.05em' }}>
                  Salta il turno
                </span>
              </motion.button>
            )}
            {pos.bottom && (
              <div style={{ transform:seat('bottom'), transformStyle:'preserve-3d' }}>
                <BhPlayerBox player={pos.bottom} isActive={currentPlayer?.user_id===pos.bottom.user_id} variant='full'/>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <BhCardEffectOverlay cardValue={cardEffect} />

      <AnimatePresence>
        {showIntro && (
          <GameIntro
            emblem="❤"
            title="Broken Hearts"
            tagline="Chi resta, ama"
            rule="Sopravvivi con almeno un cuore"
            cry="SI COMINCIA"
            players={players.map(p => p.username)}
            theme={{ accent:'#f472b6', accentSoft:'rgba(244,114,182,0.6)', text:'#ffe4f1', ring:'rgba(244,114,182,0.6)' }}
            onDone={() => { sessionStorage.setItem(`bh_intro_${gameId}`, 'done'); setShowIntro(false); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BhGame;